import React, { useCallback, useEffect } from 'react'
import { useAnalytics } from 'src/analytics/useAnalytics'
import { Spinner } from 'src/Throbber/Spinner'
import {
  CloseButton,
  MediumCancelButton,
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from '../components/Reusable/Buttons'
import {
  ModalBody,
  ModalButtonGroup,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../Modal/styles'
import { BackIcon } from '../components/ModalForms/ModalForm.styles'
import {
  initialConfirmationState,
  useConfirmationContext,
} from './ConfirmationContext'

export const Confirmation = (): JSX.Element => {
  const { confirmationState, setConfirmationState, busy } =
    useConfirmationContext()
  const { track } = useAnalytics()

  const close = useCallback(() => {
    confirmationState.onCancel && confirmationState.onCancel()
    setConfirmationState(initialConfirmationState)
  }, [confirmationState, setConfirmationState])

  const confirm = useCallback(async () => {
    await confirmationState.onConfirmCB()
    // A callback can replace this dialog with an error or another confirmation.
    // Only close the dialog whose action has actually completed.
    setConfirmationState((prev) =>
      prev === confirmationState ? initialConfirmationState : prev,
    )
  }, [confirmationState, setConfirmationState])

  const back = useCallback(() => {
    setConfirmationState(initialConfirmationState)
    confirmationState.onBack?.()
  }, [confirmationState, setConfirmationState])

  useEffect(() => {
    if (confirmationState.isShown && confirmationState.analyticsEvent) {
      track(
        confirmationState.analyticsEvent,
        confirmationState.analyticsEventData,
      )
    }
  }, [
    confirmationState.isShown,
    confirmationState.analyticsEvent,
    confirmationState.analyticsEventData,
    track,
  ])

  return (
    <ModalContainer
      show={confirmationState.isShown}
      centered
      size={confirmationState.size || 'sm'}
      backdrop="static"
      onHide={close}
    >
      <CloseButton
        position="absolute"
        top="36px"
        right="36px"
        onClick={(e) => {
          e.preventDefault()
          close()
        }}
      />

      <ModalHeader>
        <ModalTitle>{confirmationState.title}</ModalTitle>
      </ModalHeader>

      <ModalBody textStyle="body.default">{confirmationState.body}</ModalBody>

      <ModalFooter>
        {confirmationState.footer ?? <></>}
        <ModalButtonGroup>
          {confirmationState.onBack && (
            <MediumCancelButton onClick={back}>
              <BackIcon />
              Back
            </MediumCancelButton>
          )}
        </ModalButtonGroup>
        <ModalButtonGroup>
          {!confirmationState.hideCancel && (
            <StandardButton
              variant={StandardButtonVariant.SECONDARY}
              buttonSize={StandardButtonSize.MEDIUM}
              onClick={(e) => {
                e.preventDefault()
                close()
              }}
            >
              {confirmationState.cancelText || 'Cancel'}
            </StandardButton>
          )}
          {!confirmationState.hideConfirm && (
            <StandardButton
              variant={
                busy
                  ? StandardButtonVariant.SECONDARY
                  : StandardButtonVariant.PRIMARY
              }
              buttonSize={StandardButtonSize.MEDIUM}
              disabled={busy}
              onClick={(e) => {
                e.preventDefault()
                confirm()
              }}
            >
              {busy && (
                <>
                  <Spinner size="s" color="primary" />
                  &nbsp;
                </>
              )}
              {confirmationState?.confirmText || 'Confirm'}
            </StandardButton>
          )}
        </ModalButtonGroup>
      </ModalFooter>
    </ModalContainer>
  )
}
